/**
 * Every setting the site uses to talk to hirevly.app.
 *
 * These used to be hardcoded across ApplyQAEngineer (query parameter names, the
 * position -> test keyword mapping, the fallback duration, the copy on the
 * thank-you screen). They now live in one KV record so recruiters can change
 * them from the admin panel without a deploy.
 *
 * The 33 per-position test links/durations stay on their own page ("Test URLs")
 * because they are content, not integration settings.
 */
export interface HirevlySettings {
  /** Brand name used in the candidate-facing copy. */
  provider_name: string;
  /** Host every test link is expected to point at. */
  allowed_domain: string;
  /** Block saving a test link outside allowed_domain instead of just warning. */
  enforce_domain: boolean;

  /** Append the candidate's details to the test link so Hirevly pre-fills them. */
  prefill_enabled: boolean;
  param_first_name: string;
  param_last_name: string;
  param_email: string;
  param_position: string;

  /** Open the test in a new tab rather than navigating away from the site. */
  open_in_new_tab: boolean;
  /** Duration shown when a position has no duration configured. */
  default_duration_minutes: number;
  /** Hours quoted to the candidate when no link is configured for their position. */
  fallback_notice_hours: number;

  /** Keywords (comma separated, case-insensitive) that route a position to a test. */
  mapping_manual_keywords: string;
  mapping_automation_keywords: string;
  mapping_language_java: string;
  mapping_language_jsts: string;
  mapping_language_python: string;
  mapping_language_csharp: string;
}

export const DEFAULT_HIREVLY_SETTINGS: HirevlySettings = {
  provider_name: 'Hirevly',
  allowed_domain: 'hirevly.app',
  enforce_domain: false,

  prefill_enabled: true,
  param_first_name: 'name',
  param_last_name: 'lastname',
  param_email: 'email',
  param_position: 'position',

  open_in_new_tab: true,
  default_duration_minutes: 30,
  fallback_notice_hours: 24,

  mapping_manual_keywords: 'manual',
  mapping_automation_keywords: 'aqa, automation',
  mapping_language_java: 'java',
  mapping_language_jsts: 'js/ts, javascript, typescript',
  mapping_language_python: 'python',
  mapping_language_csharp: 'c#, csharp',
};

export type TestType = 'manual' | 'automation' | 'other';
export type TestLevel = 'junior' | 'middle' | 'senior' | null;
export type TestLanguage = 'java' | 'jsts' | 'python' | 'csharp' | null;

/** Split an admin-entered keyword list into lowercase terms. */
export function parseKeywords(list: string): string[] {
  return (list || '')
    .split(',')
    .map((term) => term.trim().toLowerCase())
    .filter(Boolean);
}

function matchesAny(haystack: string, list: string): boolean {
  return parseKeywords(list).some((term) => haystack.includes(term));
}

/**
 * Resolve a free-text target position to the test type/level/language whose
 * configured Hirevly link the candidate should get.
 */
export function resolveTestParams(
  position: string,
  settings: HirevlySettings
): { type: TestType; level: TestLevel; language: TestLanguage } {
  const normalized = (position || '').toLowerCase();

  let type: TestType = 'other';
  if (matchesAny(normalized, settings.mapping_manual_keywords)) {
    type = 'manual';
  } else if (matchesAny(normalized, settings.mapping_automation_keywords)) {
    type = 'automation';
  }

  let level: TestLevel = null;
  if (normalized.startsWith('junior')) level = 'junior';
  else if (normalized.startsWith('middle')) level = 'middle';
  else if (normalized.startsWith('senior')) level = 'senior';

  let language: TestLanguage = null;
  if (matchesAny(normalized, settings.mapping_language_java)) language = 'java';
  else if (matchesAny(normalized, settings.mapping_language_jsts)) language = 'jsts';
  else if (matchesAny(normalized, settings.mapping_language_python)) language = 'python';
  else if (matchesAny(normalized, settings.mapping_language_csharp)) language = 'csharp';

  return { type, level, language };
}

/** The KV key suffix for a resolved position, e.g. `automation_senior_java`. */
export function testKeySuffix(
  type: TestType,
  level: TestLevel,
  language: TestLanguage
): string | null {
  if (type === 'manual' && level) return `manual_${level}`;
  if (type === 'automation' && level && language) return `automation_${level}_${language}`;
  if (type === 'other') return 'other';
  return null;
}

/**
 * Add the candidate's details to a test link. Parameters go before any hash
 * fragment, otherwise they never reach Hirevly's server.
 */
export function buildPersonalizedTestUrl(
  testUrl: string,
  candidate: { firstName: string; lastName: string; email: string; targetPosition: string },
  settings: HirevlySettings
): string {
  if (!testUrl) return '';
  if (!settings.prefill_enabled) return testUrl;

  const hashIdx = testUrl.indexOf('#');
  const urlPart = hashIdx !== -1 ? testUrl.slice(0, hashIdx) : testUrl;
  const hashPart = hashIdx !== -1 ? testUrl.slice(hashIdx) : '';

  // encodeURIComponent, not URLSearchParams: the latter encodes a space as "+",
  // and these links are handed to a third party that was only ever sent "%20".
  const query = [
    [settings.param_first_name, candidate.firstName],
    [settings.param_last_name, candidate.lastName],
    [settings.param_email, candidate.email],
    [settings.param_position, candidate.targetPosition]
  ]
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`)
    .join('&');

  const separator = urlPart.includes('?') ? '&' : '?';
  return `${urlPart}${separator}${query}${hashPart}`;
}

/** Fill in any setting the stored record is missing (older records, new fields). */
export function withDefaults(partial: Partial<HirevlySettings> | null | undefined): HirevlySettings {
  return { ...DEFAULT_HIREVLY_SETTINGS, ...(partial || {}) };
}
